import { Component, OnInit } from '@angular/core';
import { BedrockService } from '../services/bedrock.service';
import { lastValueFrom, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SourceDocumentDialogComponent } from './source-document-dialog/source-document-dialog.component';
import { S3Service } from '../services/s3.service';
import { AlertModalComponent } from '../alert-modal/alert-modal.component';
import { Log } from '../models/prompt-log.interface';
import { DraftSectionDialogComponent } from '../draft-section-dialog/draft-section-dialog.component';
import { CollectionsService } from '../services/collections.service';
import { Model, modelList } from '../shared/llms';
import { Collection, FileMetadata } from '../models/collection.interface';
import { CollectionAttributes, FilterSets } from '../models/advisor.interface';
import { FilterModalComponent } from './filter-modal/filter-modal.component';

@Component({
  selector: 'app-advisor',
  templateUrl: './advisor.component.html',
  styleUrls: ['./advisor.component.scss']
})
export class AdvisorComponent implements OnInit {
  collectionSelectOptions: CollectionAttributes = {};
  activeFilters: CollectionAttributes = {
    state: [],
    year: [],
    docket_number: []
  };
  prompt = '';
  modelResponse = '';
  fullResponse = '';
  draft = '';
  promptSubscription?: Subscription;
  pageSizeLimit = 650;
  promptInProgress = false;
  promptFinished = false;
  logsLoading = true;
  qaLogs: Log[] = [];
  collections: Collection[] = [];

  // TODO: Add Type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sourceDocuments: any[] = [];
  promptFilename = '';
  selectedCollection: Collection | undefined;
  defaultCollectionName = 'Wildfire Mitigation';
  typingIndex = 0; // Index for typewriter effect
  typingTimeout?: NodeJS.Timeout; // Timeout ID for typing effect
  promptLoading = false;
  models = modelList;
  selectedModel: Model | null = this.models.modelList.find(model => model.id === 'claude-4-sonnet') || null;

  constructor(
    private BedrockService: BedrockService,
    public dialog: MatDialog,
    private s3Service: S3Service,
    private collectionService: CollectionsService
  ) {}

  ngOnInit() {
    this.modelResponse = '';
    this.sourceDocuments = [];
    this.getCollections();
  }

  async openFilterModal(): Promise<void> {
    const dialogRef = this.dialog.open(FilterModalComponent, {
      width: '450px',
      data: {
        options: this.collectionSelectOptions,
        currentFilters: { ...this.activeFilters }
      }
    });

    const result: CollectionAttributes = await lastValueFrom(dialogRef.afterClosed());

    if (result) {
      console.log('Received filters from modal: ', result);
      this.activeFilters = result;
    }
  }

  get filterCount(): number {
    return Object.values(this.activeFilters).flat().length;
  }

  runPrompt() {
    const modelId = this.selectedModel ? this.selectedModel.id : '';
    this.clearPromptResult();
    this.promptInProgress = true;

    if (this.promptSubscription) {
      this.promptSubscription.unsubscribe();
    }

    if (!this.selectedCollection) {
      this.openAlert('Failed', `selectedCollection is undefined`);
      this.promptInProgress = false;
      return;
    }

    this.promptSubscription = this.BedrockService.invokeBedrockModelWithContext(
      null,
      this.prompt,
      modelId,
      this.selectedCollection.collection_id,
      this.activeFilters
    ).subscribe({
      // TODO: Add Type
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (response: any) => {
        this.promptFilename = response.filename;
        this.promptLoading = true;
        this.pollPromptResponse();
      },
      error: () => {
        this.openAlert('Failed', 'Could not run prompt.');
        this.promptInProgress = false;
      }
    });
  }

  pollPromptResponse() {
    const maxAttempts = 30; // Increase this if more time is needed
    const delay = 2000; // Poll every 2 seconds
    let attempts = 0;

    const poll = () => {
      attempts++;

      if (attempts > maxAttempts) {
        this.openAlert('Failed', 'Response timed out.');
        this.promptInProgress = false;
        return;
      }

      this.s3Service.pollResult(this.promptFilename).subscribe({
        // TODO: Add Type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (response: any) => {
          if (response.status === 200) {
            if (response.body.complete === 'true') {
              this.fullResponse = response.body.response;
              this.typingIndex = this.modelResponse.length; // Start typing from the current length
              this.startTypingEffect();
              this.promptFinished = true;
              this.promptInProgress = false;
              this.sourceDocuments = response.body.source_documents
                // TODO: Add Type
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((doc: any) => {
                  if (doc.metadata && doc.metadata.source) {
                    doc.metadata.source = doc.metadata.source.split('\\').pop().split('/').pop();
                  }
                  return doc;
                })
                // TODO: Add Type
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .sort((a: any, b: any) => b.score - a.score);

              if (!this.sourceDocuments || this.sourceDocuments.length == 0) {
                this.dialog.open(AlertModalComponent, {
                  data: {
                    title: 'No Context Available',
                    message: 'There is no available source information to answer question with context.'
                  }
                });
              }
            } else {
              this.fullResponse = response.body.response;
              if (this.promptLoading && this.fullResponse.length) {
                this.promptLoading = false;
              }
              this.typingIndex = this.modelResponse.length;
              this.startTypingEffect();
              setTimeout(poll, delay); // Retry after a delay
            }
          } else {
            setTimeout(poll, delay); // Retry after a delay
          }
        },
        error: () => {
          this.openAlert('Failed', 'Error while polling.');
          this.promptInProgress = false;
        }
      });
    };

    poll();
  }

  startTypingEffect() {
    const typingSpeed = 4;

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout); // Clear any existing timeout to prevent overlapping typing
    }

    const typeCharacter = () => {
      if (this.typingIndex < this.fullResponse.length) {
        this.modelResponse += this.fullResponse.charAt(this.typingIndex);
        this.typingIndex++;
        this.typingTimeout = setTimeout(typeCharacter, typingSpeed);
      }
    };

    typeCharacter();
  }

  cancelPrompt() {
    if (this.promptSubscription) {
      this.promptSubscription.unsubscribe();
      this.promptSubscription = undefined;
      this.promptInProgress = false;
    }
  }

  clearPromptResult() {
    this.modelResponse = '';
    this.fullResponse = '';
    this.sourceDocuments = [];
    this.promptFinished = false;
  }

  setTemplate(option: string): void {
    this.prompt = option;
  }

  // TODO: Add Type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  openSourceDocumentDialog(doc: any): void {
    this.dialog.open(SourceDocumentDialogComponent, {
      width: '1200px',
      height: 'auto',
      data: doc
    });
  }

  copyToClipboard(): void {
    navigator.clipboard.writeText(this.modelResponse).then(
      () => {
        console.log('Copying to clipboard was successful!');
      },
      err => {
        console.error('Could not copy text: ', err);
      }
    );
  }

  getLogs(collection_id: string) {
    this.logsLoading = true;
    this.s3Service.getFiles('advisor', collection_id).subscribe({
      next: (data: Log[]) => {
        this.qaLogs = data;
      },
      error: error => {
        console.error('There was an error while fetching the logs: ', error);
      },
      complete: () => {
        this.logsLoading = false;
      }
    });
  }

  getCollections() {
    this.collectionService.getCollections().subscribe({
      next: data => {
        this.collections = data;
        this.selectedCollection = this.collections.find(collection => collection.name === this.defaultCollectionName);
        if (this.selectedCollection) {
          this.setCollection(this.selectedCollection);
          return;
        }
        throw new Error(`Could not find collection ${this.defaultCollectionName}`);
      },
      error: error => {
        console.error('There was an error while fetching the collections: ', error);
      }
    });
  }

  setQuestion(option: string): void {
    this.prompt = option;
  }

  draftSection(): void {
    const dialogRef = this.dialog.open(DraftSectionDialogComponent, {
      width: '1000px',
      height: '750px',
      data: {
        context: this.modelResponse,
        documents: this.sourceDocuments,
        filename: this.promptFilename
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.draft = result;
      console.log(this.draft);
    });
  }

  openAlert(title: string, message: string): void {
    this.dialog.open(AlertModalComponent, {
      data: {
        title: title,
        message: message
      }
    });
  }

  resetLogsDisplay() {
    this.qaLogs = [];
  }

  resetFilters() {
    this.activeFilters = {
      state: [],
      year: []
    };
    this.collectionSelectOptions = {};
  }

  // Gets logs for a collection and populates filters with appropriate options
  setCollection(collection: Collection): void {
    this.resetLogsDisplay();
    this.resetFilters();
    this.getLogs(collection.collection_id);
    this.fetchFilterOptions(collection.collection_id);
  }

  get hasFilterOptions(): boolean {
    return Object.keys(this.collectionSelectOptions).length > 0;
  }

  // Helper to allow filter selects to be sorted
  _setToSortedArray(set: Set<string>): string[] {
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  fetchFilterOptions(collection_id: string) {
    // Populate filter options into this.collectionSelectOptions
    this.collectionService.getCollectionData(collection_id).subscribe({
      next: (data: FileMetadata[]) => {
        const filterSets: FilterSets = {
          state: new Set<string>(),
          year: new Set<string>(),
          docket_number: new Set<string>()
        };

        // Get only unique values
        data.forEach((item: FileMetadata) => {
          // Skip if no catalogue
          if (!item.catalogue) return;
          Object.keys(filterSets).forEach(key => {
            const valueToAdd = item.catalogue && item.catalogue[key];
            // Remove null values
            if (!valueToAdd) return;
            filterSets[key].add(valueToAdd);
          });
        });

        // Convert set to array and sort
        Object.keys(filterSets).forEach(key => {
          this.collectionSelectOptions[key] = this._setToSortedArray(filterSets[key]);
        });
      },
      error: error => {
        console.error('There was an error while fetching the catalogue: ', error);
      }
    });
  }

  selectModel(model: Model | null) {
    this.selectedModel = model;
  }
}
